import { useCallback, useEffect, useMemo, useState } from 'react';
import { getMerchants } from '../api/client';

const STATUS_BADGES = {
  active: 'text-bg-success',
  inactive: 'text-bg-secondary',
  suspended: 'text-bg-danger',
};

// The backend returns the whole merchant list in one response (see
// P2MDashboard::merchants()'s docblock - no server-side pagination on this
// endpoint), so pagination here is purely client-side over the
// already-fetched, already-filtered array, same as the search/status filter.
const PER_PAGE = 20;

// Sortable columns (2026-08-07) - every visible column, in table order.
// `key` matches the field name on each merchant record returned by the API.
const COLUMNS = [
  { key: 'merchant_id', label: 'Merchant ID' },
  { key: 'merchant_name', label: 'Name' },
  { key: 'category_code', label: 'Category code' },
  { key: 'account_no', label: 'Account no' },
  { key: 'status', label: 'Status' },
];

// `numeric: true` makes this collator compare embedded digit runs by value
// rather than lexicographically (so "SPORTSPLUS00200" sorts before
// "SPORTSPLUS01000", and Account no sorts by magnitude rather than by
// leading digit) - useful for both the mostly-alphanumeric ID/name columns
// and the purely-numeric account number column, without needing a separate
// numeric-vs-text comparator per column.
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

export default function Merchants() {
  const [merchants, setMerchants] = useState([]);
  const [meta, setMeta] = useState({ type: null, key: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  // Default sort: Name, ascending - matches how the list reads best on
  // first load, before anyone has clicked a column header.
  const [sortField, setSortField] = useState('merchant_name');
  const [sortDir, setSortDir] = useState('asc');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getMerchants();
      setMerchants(data.merchants || []);
      setMeta({ type: data.type, key: data.key });
    } catch (err) {
      setError(err.message || 'Failed to load merchants.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const statuses = useMemo(() => {
    const set = new Set(merchants.map((m) => m.status).filter(Boolean));
    return Array.from(set).sort();
  }, [merchants]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return merchants.filter((m) => {
      if (statusFilter !== 'all' && m.status !== statusFilter) {
        return false;
      }
      if (!q) {
        return true;
      }
      return [m.merchant_id, m.merchant_name, m.account_no, m.client_id]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q));
    });
  }, [merchants, query, statusFilter]);

  const sorted = useMemo(() => {
    if (!sortField) {
      return filtered;
    }
    const list = [...filtered];
    list.sort((a, b) => {
      const result = collator.compare(String(a[sortField] ?? ''), String(b[sortField] ?? ''));
      return sortDir === 'asc' ? result : -result;
    });
    return list;
  }, [filtered, sortField, sortDir]);

  // Reset to page 1 whenever the result set changes shape (new search,
  // status filter, or a fresh load) - otherwise a filter that narrows the
  // list could leave the user stranded on a now-nonexistent page. Sorting
  // deliberately doesn't reset the page - it reorders the same result set,
  // it doesn't change how many pages there are.
  useEffect(() => {
    setPage(1);
  }, [query, statusFilter, merchants]);

  function handleSort(field) {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  const totalPages = Math.max(Math.ceil(sorted.length / PER_PAGE), 1);
  const pageSafe = Math.min(page, totalPages);
  const paged = useMemo(
    () => sorted.slice((pageSafe - 1) * PER_PAGE, pageSafe * PER_PAGE),
    [sorted, pageSafe]
  );

  return (
    <div>
      <h1 className="h4 mb-2">Merchants</h1>
      <p className="text-muted small" style={{ maxWidth: '640px' }}>
        Merchant list push by P2M Java to Router
      </p>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {!loading && !error && meta.type === 'none' && (
        <div className="alert alert-warning py-2">
          No data found at the expected Redis key ({meta.key}). Either the key doesn&rsquo;t exist
          in this environment yet, or the naming convention this page assumes is wrong - see
          P2MDashboard::merchants()&rsquo;s docblock.
        </div>
      )}

      <div className="d-flex flex-wrap gap-2 mb-3">
        <input
          type="text"
          className="form-control"
          style={{ maxWidth: '320px' }}
          placeholder="Search merchant ID, name, account no, client ID"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select
          className="form-select"
          style={{ maxWidth: '180px' }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All statuses</option>
          {statuses.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button className="btn btn-outline-secondary btn-sm" onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="d-flex align-items-center gap-2 text-muted py-4">
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          Loading…
        </div>
      ) : (
        <div className="card border-0 shadow-sm">
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      role="button"
                      onClick={() => handleSort(col.key)}
                      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                    >
                      {col.label}
                      <span className="text-muted">
                        {sortField === col.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paged.map((m) => (
                  <tr key={m.merchant_id}>
                    <td className="text-truncate" style={{ maxWidth: '10rem' }} title={m.merchant_id || ''}>
                      {m.merchant_id ?? '—'}
                    </td>
                    <td>{m.merchant_name ?? '—'}</td>
                    <td>{m.category_code ?? '—'}</td>
                    <td>{m.account_no ?? '—'}</td>
                    <td>
                      {m.status ? (
                        <span className={`badge ${STATUS_BADGES[m.status] || 'text-bg-secondary'}`}>
                          {m.status}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      {merchants.length === 0 ? 'No merchants found.' : 'No merchants match this search/filter.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && merchants.length > 0 && (
        <div className="d-flex align-items-center justify-content-between mt-2 flex-wrap gap-2">
          <p className="text-muted small mb-0">
            Showing {paged.length === 0 ? 0 : (pageSafe - 1) * PER_PAGE + 1}
            &ndash;{(pageSafe - 1) * PER_PAGE + paged.length} of {sorted.length} filtered
            (of {merchants.length} total).
          </p>
          <nav className="d-flex align-items-center gap-3">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={pageSafe <= 1}
            >
              Previous
            </button>
            <span className="text-muted small">
              Page {pageSafe} of {totalPages}
            </span>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              disabled={pageSafe >= totalPages}
            >
              Next
            </button>
          </nav>
        </div>
      )}
    </div>
  );
}
